import { Box, Paper, TableBody, TableContainer, TableHead, Table as TableRoot } from '@mui/material'
import {
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  HeaderGroup,
  Row,
  RowData,
  useReactTable,
} from '@tanstack/react-table'
import { useState } from 'react'

import { useTopScrollbar } from 'components/Scrollbar'
import Pagination from '../Pagination'
import { useTableSort } from '../Sort'
import { EmptyTableFooter } from './components/EmptyTableFooter'
import { FooterInfo } from './components/FooterInfo'
import { HeaderGroups } from './components/Header'
import { LoadingCell } from './components/LoadingCell'
import { NotFoundCell } from './components/NotFoundCell'
import { Rows } from './components/Rows'
import {
  MIN_ESTIMATED_STICKY_ROWS,
  useStickyHeaderStyles,
} from './components/useStickyHeaderStyles'
import { TableProps } from './types'
import { collectLeftPinnedColumns, getRowId, getSubRowsHandler, updateData } from './utils'

export const Table = <T extends RowData>({
  columns,
  columnVisibility,
  data,
  footerInfo: foterInfoProps,
  loading,
  meta,
  onClickRow,
  onClickRowAsync,
  onUpdateData,
  pagination: paginationProps,
  sort,
  ...muiTableContainerProps
}: TableProps<T>): JSX.Element => {
  const [expanded, setExpanded] = useState<ExpandedState>(meta?.expanded ?? {})
  const { setSorting, sorting } = useTableSort<T>(sort)

  // FIXME: react-hooks/incompatible-library
  const table = useReactTable<T>({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: getRowId<T>,
    getSubRows: getSubRowsHandler<T>,
    initialState: {
      columnPinning: {
        left: collectLeftPinnedColumns<T>(columns),
      },
    },
    meta: {
      ...meta,
      updateData: updateData(onUpdateData),
    },
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,

    state: {
      columnVisibility,
      expanded,
      sorting,
    },
  })

  const { getAllColumns, getHeaderGroups, getRowModel } = table
  const rows: Row<T>[] = getRowModel().rows
  const rowsCount: number = rows.length
  const columnsCount: number = getAllColumns().length
  const headerGroups: HeaderGroup<T>[] = getHeaderGroups()

  const withStickyHeader = Boolean(meta?.stickyHeader) && rowsCount > MIN_ESTIMATED_STICKY_ROWS
  const tableContainerStickyStyles = useStickyHeaderStyles({
    headerGroups: headerGroups.length,
    variant: 'tableContainer',
  })
  const tableHeadStickyStyles = useStickyHeaderStyles({ variant: 'tableHead' })

  const { onSourceScroll, sourceRef: tableContainerRef, TopScrollbar } = useTopScrollbar()

  return (
    <Box>
      {TopScrollbar}

      <TableContainer
        component={Paper}
        onScroll={onSourceScroll}
        ref={tableContainerRef}
        sx={{
          scrollbarWidth: 'none',
          ...(withStickyHeader ? tableContainerStickyStyles : {}),
        }}
        {...muiTableContainerProps}
      >
        <TableRoot stickyHeader>
          <TableHead sx={withStickyHeader ? tableHeadStickyStyles : {}}>
            <HeaderGroups
              headerGroups={headerGroups}
              sort={sort}
            />
          </TableHead>

          <TableBody>
            {loading ? (
              <LoadingCell columnsCount={columnsCount} />
            ) : rowsCount > 0 ? (
              <Rows {...{ onClickRow, onClickRowAsync, rows }} />
            ) : (
              <NotFoundCell columnsCount={columnsCount} />
            )}
          </TableBody>
        </TableRoot>
      </TableContainer>

      <EmptyTableFooter>
        {foterInfoProps && <FooterInfo {...foterInfoProps} />}
        {paginationProps && <Pagination {...paginationProps} />}
      </EmptyTableFooter>
    </Box>
  )
}
